import './xeroCreditNotesReceivableForm.scss';

import { ScrollableArea } from '@approvalmax/ui';
import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import SendToSupplierFileDropArea from 'pages/requestForm/components/SendToSupplierFileDropArea';
import useShowDropArea from 'pages/requestForm/hooks/useShowDropArea';
import {
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
    RequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { FC } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { useUserBetaFeatures } from 'shared/hooks';

import { uploadAttachments } from '../../../actions';
import { AttachmentsSection, NoteForApproversSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import XeroLineItemsSection from '../XeroLineItemsSection/XeroLineItemsSection';
import { FieldsSection } from './components/FieldsSection/FieldsSection';
import { messages } from './XeroCreditNotesReceivableForm.messages';

const { xeroConstants } = constants;
const bem = bemFactory.block('reqf-xero-bill-form');

interface XeroCreditNotesReceivableFormProps {
    className?: string;
    request: domain.XeroCreditNotesReceivableRequest;
}

export const XeroCreditNotesReceivableForm: FC<XeroCreditNotesReceivableFormProps> = (props) => {
    const { className, request } = props;

    const dispatch = useDispatch();
    const context = useSelector(getContext);
    const [attachmentFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroAttachment])
    );

    const showDropArea = useShowDropArea();
    const editMode = useSelector((state) => getRequestEditMode(state, request));

    const { isBelgradeUIFilesUploadSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider
                    className={bem.add(className)()}
                    onUpload={(files, attachmentKind) => dispatch(uploadAttachments({ files, attachmentKind }))}
                    maxSize={context.attachmentMaxSize}
                    allowedFileExtensions={xeroConstants.XERO_ALLOWED_FILE_EXTENSIONS}
                >
                    {children}

                    {showDropArea && <SendToSupplierFileDropArea isSupplierAreaActive={false} />}
                </FileDropProvider>
            )}
        >
            <div className={bem('toolbar')}>
                <div className={bem('toolbar-header')}>
                    <div className={bem('toolbar-header-text')}>{messages.headerText}</div>

                    <ActionsBlock />
                </div>

                <FieldsSection request={request} />
            </div>

            <ScrollableArea className={bem('content')}>
                <div className={bem('content-inner')}>
                    <RequesterInstructionSection instructionText={context.requesterInstruction} />

                    <ConditionWrapper
                        condition={isBelgradeUIFilesUploadSection}
                        wrapperIf={(children) => (
                            <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                                {children}

                                <AttachmentsSection
                                    request={request}
                                    editMode={editMode}
                                    maxFilesInList={9}
                                    extensions={xeroConstants.xeroAllowedFileExtensions}
                                    readOnly={attachmentFieldPermission.disabled}
                                    required={attachmentFieldPermission.required}
                                />
                            </Grid>
                        )}
                    >
                        {editMode !== RequestEditMode.Editor && <NoteForApproversSection />}
                    </ConditionWrapper>

                    <XeroLineItemsSection request={request} />

                    {!isBelgradeUIFilesUploadSection && (
                        <FilesSection
                            editMode={editMode}
                            maxFilesCount={9}
                            readonly={attachmentFieldPermission.disabled}
                            required={attachmentFieldPermission.required}
                        />
                    )}
                </div>
            </ScrollableArea>
        </ConditionWrapper>
    );
};

XeroCreditNotesReceivableForm.displayName = 'XeroCreditNotesReceivableForm';
